import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import type {
	IAssemblyServicesData,
	IDexfSettings,
	ILocalizedInformation,
	ITheme
} from '@inter-ikea-kompis/types';
import { DexfSettingsUtility } from '@inter-ikea-kompis/utilities';
import type { IKompisElementPropertyValue, RenderResult } from '@inter-ikea-kompis/web-component';

import { Text } from '@inter-ikea-kompis/component-text';

import { FocusRing, Hyperlink } from '@ingka/common-styles-webc';
import { drill, registerIcon } from '@ingka/icon-store';
import { Icon as SkapaIcon } from '@ingka/icon-webc/passive.js';

import AssemblyServicesTracker from '../../trackers/AssemblyServicesTracker.js';
import type IAssemblyServicesAnalyticsProp from '../../types/IAssemblyServicesAnalyticsProp.js';
import type IAssemblyServicesTranslations from '../../types/IAssemblyServicesTranslations.js';
import AssemblyServicesStatusButton from '../assembly-services-status-button/AssemblyServicesStatusButton.js';
import AssemblyServicesStyle from './AssemblyServicesStyle.js';

/**
 * @event assemblyServicesClick User clicks on the assembly services status.
 *
 * @tracker packages/component-assembly-services/src/trackers/AssemblyServicesTracker.ts
 */

export default class AssemblyServices extends KompisElement {
	// Theme.
	@property({ type: Object, required: true })
	public theme!: ITheme;

	// Assembly services data, containing if assembly services are available or not.
	@property({ type: Object })
	public assemblyServicesData: IAssemblyServicesData | null = null;

	// Localized information. Deprecated - use "translations" and "settings" instead.
	@property({ type: Object })
	public localizedInformation: ILocalizedInformation | null = null;

	// The translations retrieved from translations service.
	@property({ type: Object })
	public translations: IAssemblyServicesTranslations | null = null;

	// The settings retrieved from settings service.
	@property({ type: Object })
	public settings: IDexfSettings | null = null;

	// Submitted and validated ZIP code.
	@property({ type: String })
	public zipCode: string | null = null;

	// Disables links. This attribute should be set to "true" when in a Kiosk.
	@property({ type: Boolean })
	public disableLinks = false;

	// Used for automatic analytics. If null or not set, analytics is turned off.
	@property({ type: Object })
	public analytics: IAssemblyServicesAnalyticsProp | null = null;

	private tracker: AssemblyServicesTracker | null = null;

	private dexfSettings: IDexfSettings | null = null;

	public override render(): RenderResult {
		// We want translations and settings to be required, but since all props to provide translations and settings are optional, this check is needed until localizedInformation is removed.
		if (!this.hasTranslationsAndSettings()) {
			// We can't throw an error because on the first render no props are set.
			return null;
		}

		return html`
			<style>
				${Hyperlink.CSS + FocusRing.CSS}
				${AssemblyServicesStyle.getStyle({ theme: this.theme })}
			</style>

			${this.getIcon()}

			<div>${this.getTitle()} ${this.getAssemblyServicesStatusButton()} ${this.getInfoLink()}</div>
		`;
	}

	public override disconnectedCallback(): void {
		super.disconnectedCallback();
		this.disconnectTracker();
	}

	public override async connectedCallback(): Promise<void> {
		registerIcon(drill);

		this.customElements.define('skapa-icon', SkapaIcon);
		this.customElements.define('kompis-text', Text);
		this.customElements.define(
			'kompis-assembly-services-status-button',
			AssemblyServicesStatusButton
		);

		super.connectedCallback();
	}

	protected override shouldUpdate(
		changedProperties: Map<string, IKompisElementPropertyValue>
	): boolean {
		if (
			changedProperties.has('localizedInformation') &&
			this.localizedInformation &&
			!this.settings
		) {
			this.dexfSettings = DexfSettingsUtility.parseLocalizedInformation(this.localizedInformation);
		}

		if (changedProperties.has('settings') && this.settings) {
			this.dexfSettings = this.settings;
		}

		if (changedProperties.has('analytics') && this.hasTranslationsAndSettings()) {
			this.disconnectTracker();
			this.connectTracker();
		}

		return true;
	}

	private hasTranslationsAndSettings(): boolean {
		return (!!this.translations && !!this.settings) || !!this.localizedInformation;
	}

	private getSettings(): IDexfSettings {
		return <IDexfSettings>this.dexfSettings;
	}

	private getTranslations(): IAssemblyServicesTranslations {
		const translations = this.translations
			? this.translations
			: <IAssemblyServicesTranslations>this.localizedInformation?.translations;

		return translations;
	}

	private async connectTracker(): Promise<void> {
		if (this.analytics) {
			this.tracker = new AssemblyServicesTracker({
				...this.analytics,
				settings: this.getSettings()
			});

			await this.tracker?.connect();
			// Make sure child components with analytics connect their trackers when the id is available.
			this.requestUpdate();
		}
	}

	private disconnectTracker(): void {
		this.tracker?.disconnect();
		this.tracker = null;
	}
	private getIcon(): RenderResult {
		return html` <skapa-icon icon="drill"></skapa-icon> `;
	}

	private getTitle(): RenderResult {
		return html`<h3>${this.getTranslations().assemblyServicesTitle}</h3>`;
	}

	private getAssemblyServicesStatusButton(): RenderResult {
		return html`<kompis-assembly-services-status-button
			.theme="${this.theme}"
			.translations="${this.translations}"
			.assemblyServicesData="${this.assemblyServicesData}"
			.zipCode="${this.zipCode}"
			.localizedInformation="${this.localizedInformation}"
			@statusButtonClick="${() => {
				this.dispatchEvent(
					new CustomEvent('assemblyServicesClick', {
						bubbles: true
					})
				);
				this.sendAssemblyServicesClick();
			}}"
		></kompis-assembly-services-status-button>`;
	}

	private getInfoLink(): RenderResult {
		const url = this.getSettings().kompis.urls.assemblyServicesLink;

		if (!url || this.disableLinks || !this.assemblyServicesData || !this.zipCode) {
			return null;
		}

		return html`
			<a
				class="${Hyperlink.classnames()}"
				href="${url}"
				target="_blank"
				@click="${() => this.tracker?.sendLearnMoreNavigation()}"
				>${this.getTranslations().assemblyServicesLearnMore}</a
			>
		`;
	}

	private sendAssemblyServicesClick(): void {
		this.tracker?.sendAssemblyServicesClick();
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'skapa-icon': SkapaIcon;
		'kompis-assembly-services': AssemblyServices;
	}
}
